import { EngagementOnPostChartView } from './EngagementOnPostChart.view';
import React, { useContext, useEffect, useState } from 'react';
import { getDate, parse } from 'date-fns';
import { BarChartData } from '../../../BarChart/BarChart.controller';
import { GetPostStatsLazyQueryHookResult, PostStatsByProfile } from '../../../../../models/graphql';
import { AnalyticsContext } from '../../../AnalyticsProvider';
import { Metrics, getAvailableMetrics } from '../../helpers/chartHelpers';
import { DateStringFormat } from 'constants/time-formats';

type EngagementOnPostChartControllerProps = {
  data: GetPostStatsLazyQueryHookResult[1]['data'];
  loading: boolean;
};
export enum EngagementTab {
  'engagement' = 'engagement',
  'likes' = 'likes',
  'comments' = 'comments',
  'shares' = 'shares',
  'clicks' = 'clicks'
}

type EngagementAccumulator = BarChartData & {
  likes?: number | null;
  shares?: number | null;
  comments?: number | null;
};
const getEngagementCount = (
  postStatsByProfile: PostStatsByProfile[],
  dates: Date[],
  activeTab: EngagementTab
): BarChartData[] => {
  return dates.map((date, indexDay) => {
    return postStatsByProfile.reduce<EngagementAccumulator>(
      (acc, currentProfile) => {
        const postStats = currentProfile.postStats[indexDay];
        const { likes: accLikes, shares: accShares, comments: accComments } = acc;
        const { likes: currentLikes, shares: currentShares, comments: currentComments } = postStats;
        const rangeStart = parse(postStats.rangeStartDate, DateStringFormat, new Date());
        const rangeEnd = parse(postStats.rangeEndDate, DateStringFormat, new Date());
        const likes = typeof accLikes === 'number' ? accLikes + (currentLikes ?? 0) : currentLikes;
        const shares = typeof accShares === 'number' ? accShares + (currentShares ?? 0) : currentShares;
        const comments = typeof accComments === 'number' ? accComments + (currentComments ?? 0) : currentComments;
        if (activeTab === EngagementTab.likes) {
          return {
            ...acc,
            likes,
            rangeStart,
            rangeEnd
          };
        } else if (activeTab === EngagementTab.shares) {
          return { ...acc, shares, rangeStart, rangeEnd };
        } else if (activeTab === EngagementTab.comments) {
          return { ...acc, comments, rangeStart, rangeEnd };
        } else {
          return { ...acc, likes, comments, shares, rangeStart, rangeEnd };
        }
      },
      {
        date,
        day: getDate(date),
        timeInMs: date.getTime(),
        rangeStart: new Date(),
        rangeEnd: new Date()
      }
    );
  });
};

export const metricToTab = {
  [Metrics.likes]: EngagementTab.likes,
  [Metrics.comments]: EngagementTab.comments,
  [Metrics.shares]: EngagementTab.shares
};

const getMaxValueAxisY = (chartData: BarChartData[], activeTab: EngagementTab) => {
  if (activeTab === EngagementTab.comments) {
    return Math.max(...chartData.map(data => (typeof data.comments === 'number' ? data.comments : 0)));
  }
  if (activeTab === EngagementTab.likes) {
    return Math.max(...chartData.map(data => (typeof data.likes === 'number' ? data.likes : 0)));
  }
  if (activeTab === EngagementTab.shares) {
    return Math.max(...chartData.map(data => (typeof data.shares === 'number' ? data.shares : 0)));
  }
  return Math.max(
    ...chartData.map(data => {
      if (typeof data.shares === 'number' && typeof data.likes === 'number' && typeof data.comments === 'number') {
        return data.shares + data.likes + data.comments;
      }
      return 0; // We shouldnt reach here since it will always be a number
    })
  );
};
export const EngagementOnPostChartController = ({ loading, data }: EngagementOnPostChartControllerProps) => {
  const currentDate = new Date();
  const { socialProfiles, selectedProfilesIds } = useContext(AnalyticsContext);
  const selectedProfilesPrimitiveValue = JSON.stringify(selectedProfilesIds);
  const [activeTab, setActiveTab] = useState<EngagementTab>(EngagementTab.engagement);
  const postStatsByProfile = data?.analyticsPostStats.postStatsByProfile ?? [];

  useEffect(() => setActiveTab(EngagementTab.engagement), [selectedProfilesPrimitiveValue]);

  if (postStatsByProfile.length >= 1) {
    const firstPostStats = postStatsByProfile[0].postStats;
    const dates = firstPostStats.map(postObj => parse(postObj.rangeEndDate, DateStringFormat, currentDate));
    const firstDateFromData = firstPostStats[0].rangeStartDate;
    const lastDateFromData = firstPostStats[firstPostStats.length - 1].rangeEndDate;
    const postCount = postStatsByProfile.reduce((acc, profile) => {
      const postCountSum = profile.postStats.reduce((sum, postStats) => sum + postStats.postCount, 0);
      return acc + postCountSum;
    }, 0);
    const chartData = getEngagementCount(postStatsByProfile, dates, activeTab);
    const selectedSocialProfiles = socialProfiles.filter(profile => selectedProfilesIds.includes(profile.id));
    const chartFromAllTabs = getEngagementCount(postStatsByProfile, dates, EngagementTab.engagement);
    const isEveryElementNull =
      !chartFromAllTabs.some(
        data => typeof data.comments === 'number' || typeof data.likes === 'number' || typeof data.shares === 'number'
      ) || postCount === 0;

    const availableMetrics = getAvailableMetrics(selectedSocialProfiles);
    const activeMetrics =
      activeTab === EngagementTab.engagement
        ? availableMetrics
        : availableMetrics.filter(metric => metricToTab[metric] === activeTab);
    const maxValueAxisY = getMaxValueAxisY(chartData, activeTab);

    return (
      <EngagementOnPostChartView
        activeTab={activeTab}
        isEmpty={isEveryElementNull || selectedProfilesIds.length === 0}
        setActiveTab={setActiveTab}
        loading={loading}
        chartData={chartData}
        availableMetrics={availableMetrics}
        activeMetrics={activeMetrics}
        datesChart={dates}
        firstDateFromData={firstDateFromData}
        lastDateFromData={lastDateFromData}
        maxValueAxisY={maxValueAxisY}
      />
    );
  }

  return (
    <EngagementOnPostChartView
      activeTab={activeTab}
      isEmpty={true}
      setActiveTab={setActiveTab}
      loading={loading}
      chartData={[]}
      activeMetrics={[]}
      availableMetrics={[]}
      datesChart={[]}
      firstDateFromData={''}
      lastDateFromData={''}
      maxValueAxisY={0}
    />
  );
};
